/**
 * Which dice skins a player is entitled to. Kept apart from
 * `diceSkins.data.js` on purpose: that file is frozen lab output and holds
 * only the recipe, which is what a skin looks like. What a skin *costs* is
 * a shop concern, and it changes on a different clock, so it lives here.
 *
 * The mapping mirrors `PLAYMAT_THEMES`: a client-side id carries the
 * `cosmeticId` the server grants, and the two stay in step by convention.
 * A key with no entry, or one marked `free`, is available to everyone.
 * That covers the three paper colours, which are not skins at all.
 */
import { peekCosmeticsCached } from '../../arena/cosmeticsCache';

export const DICE_SKIN_ENTITLEMENTS = {
  'apprentice-wizard': { free: true },
  'avatar-of-air': { cosmeticId: 'dice-avatar-of-air' },
  'avatar-of-earth': { cosmeticId: 'dice-avatar-of-earth' },
  'avatar-of-fire': { cosmeticId: 'dice-avatar-of-fire' },
  'avatar-of-water': { cosmeticId: 'dice-avatar-of-water' },
  flamecaller: { cosmeticId: 'dice-flamecaller' },
  geomancer: { cosmeticId: 'dice-geomancer' },
  waveshaper: { cosmeticId: 'dice-waveshaper' },
  sparkmage: { cosmeticId: 'dice-sparkmage' },
  savior: { cosmeticId: 'dice-savior' },
  necromancer: { cosmeticId: 'dice-necromancer' },
  persecutor: { cosmeticId: 'dice-persecutor' },
  harbinger: { cosmeticId: 'dice-harbinger' },
};

/**
 * Whether this client may paint dice in `id`.
 *
 * **Fails open on a cold cache**, the same posture `getPlaymatUrl()` takes:
 * a player who picked a skin they own should not be flashed back to bone
 * for the second it takes the catalog to arrive. The exposure is nil, since
 * the palette is local and the opponent renders their own dice, so there is
 * nothing to gain by forcing it. `boardLifecycle` then repaints as soon as
 * the cache resolves, which takes the skin back off an unentitled client.
 */
export function isDiceSkinOwned(id) {
  const entitlement = DICE_SKIN_ENTITLEMENTS[id];
  if (!entitlement || entitlement.free) return true;
  const cache = peekCosmeticsCached();
  if (!cache) return true;
  const owned = Array.isArray(cache.owned) ? cache.owned : [];
  return owned.includes(entitlement.cosmeticId);
}

/** The `cosmeticId` a skin is sold under, or `null` if it is not for sale. */
export function diceSkinCosmeticId(id) {
  const entitlement = DICE_SKIN_ENTITLEMENTS[id];
  return entitlement && !entitlement.free ? entitlement.cosmeticId : null;
}
